import { useState, useEffect } from 'react';
import {
  Card,
  CardHeader,
  CardTitle,
  CardContent,
  Button,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  Badge
} from '@etherisc/ui-kit';
import { 
  InfoIcon, 
  ShieldIcon, 
  TrendingUpIcon, 
  UsersIcon 
} from 'lucide-react';
import { LoadingFactorsTable } from './LoadingFactorsTable';
import { StakeholdersTable } from './StakeholdersTable';
import { CoverageConfigForm } from './CoverageConfigForm';
import { OverviewForm } from './OverviewForm';
import { NewEnforcementTable } from './NewEnforcementTable';
import { useProductConfigData } from '../../hooks/useProductConfigData';
import type { 
  ProductConfig, 
  LoadingConfig, 
  StakeholdersAnrpDistribution,
  AdminUnitLoadingFactor,
  CoverageConfig,
  PerilWeighting,
  CoverageLevelLimits,
  AutomationConfig
} from '../../types/product';

interface ProductConfigFormProps {
  productConfig: ProductConfig;
  onChange: (config: ProductConfig) => void;
  productName?: string;
  productCode?: string;
  productDescription?: string;
  productStatus?: string;
  productTags?: string[];
  productVersion?: string;
  quotationScript?: string;
  claimsScript?: string;
  onProductFieldChange?: (field: string, value: unknown) => void;
  errors?: Array<{
    path: string;
    message: string;
    value: unknown;
  }>;
  readOnly?: boolean;
  cronExpression?: string;
  onCronExpressionChange?: (cron: string) => void;
}

export function ProductConfigForm({ 
  productConfig, 
  onChange,
  productName = '',
  productCode = '',
  productDescription = '',
  productStatus = 'DRAFT',
  productTags = [],
  productVersion = '',
  quotationScript = '',
  claimsScript = '',
  onProductFieldChange,
  errors = [],
  readOnly = false,
  cronExpression,
  onCronExpressionChange,
}: ProductConfigFormProps) {
  const [activeTab, setActiveTab] = useState('overview');
  const { adminUnits, allAdminUnits, organizations, loading, error } = useProductConfigData();

  // Auto-configure MyUbuntu organization when organizations are loaded
  useEffect(() => {



    


    // Ensure stakeholders structure exists before proceeding
    if (!productConfig.stakeholdersAnrpDistribution) {

      const updatedConfig = {
        ...productConfig,
        stakeholdersAnrpDistribution: {
          currency: 'USD', // Default currency
          administration: {
            insurer: {
              organizationId: '',
              name: '',
              percentage: 0,
              role: 'Insurer' as const
            },
            myubuntu: {
              organizationId: '',
              name: '',
              percentage: 0,
              role: 'MyUbuntu' as const
            }
          },
          underwriters: [],
          brokers: [],
          calculatingAgents: [],
          otherOrganisations: []
        }
      };
      onChange(updatedConfig);
      return; // Let the next render handle the auto-config
    }

    if (organizations.length > 0 && !productConfig.stakeholdersAnrpDistribution?.administration?.myubuntu?.organizationId) {
      // Find MyUbuntu organization by exact match only
      const myubuntuOrg = organizations.find(org => org.code === 'default' && org.type === 'INSURANCE_COMPANY');
      
      if (myubuntuOrg) {
        // Update the product config with the MyUbuntu organization ID
        const updatedConfig = {
          ...productConfig,
          stakeholdersAnrpDistribution: {
            ...productConfig.stakeholdersAnrpDistribution,
            administration: {
              ...productConfig.stakeholdersAnrpDistribution.administration,
              myubuntu: {
                ...productConfig.stakeholdersAnrpDistribution.administration.myubuntu,
                organizationId: myubuntuOrg.id,
                name: myubuntuOrg.name
              }
            }
          }
        };
        onChange(updatedConfig);
      }
    }
  }, [organizations, productConfig, onChange]);

  // Show error state if API fails
  if (error) {
    return (
      <div className="p-6 bg-red-50 border border-red-200 rounded-lg">
        <h3 className="text-lg font-medium text-red-800 mb-2">Configuration Data Error</h3>
        <p className="text-red-700 mb-4">{error}</p>
        <p className="text-sm text-red-600">
          Please check that the API endpoints are available:
          <br />• /api/administrative-units?active=true
          <br />• /api/organizations?active=true
        </p>
      </div>
    );
  }

  // Tab configuration
  const tabs = [
    { value: 'overview', label: 'Overview', icon: InfoIcon },
    { value: 'coverage', label: 'Coverage', icon: ShieldIcon },
    { value: 'loading', label: 'Loading', icon: TrendingUpIcon },
    { value: 'stakeholders', label: 'Stakeholders', icon: UsersIcon }
  ];

  // Section-specific change handlers
  const handleCoverageConfigChange = (coverageConfig: CoverageConfig) => {
    onChange({
      ...productConfig,
      coverageConfig
    });
  };

  // Removed: handlePricingFactorsChange - pricing tab removed

  const handleLoadingFactorsChange = (loadingFactors: AdminUnitLoadingFactor[]) => {

    const newLoadingConfig: LoadingConfig = {
      ...productConfig.loadingConfig,
      loadingFactors
    };
    
    const newProductConfig = {
      ...productConfig,
      loadingConfig: newLoadingConfig
    };
    

    onChange(newProductConfig);
  };

  const handleStakeholdersChange = (stakeholders: StakeholdersAnrpDistribution) => {
    onChange({
      ...productConfig,
      stakeholdersAnrpDistribution: stakeholders
    });
  };

  const handleLoadingConfigSettingsChange = (field: keyof LoadingConfig, value: number) => {
    const newLoadingConfig: LoadingConfig = {
      ...productConfig.loadingConfig,
      [field]: value
    };
    
    onChange({
      ...productConfig,
      loadingConfig: newLoadingConfig
    });
  };

  const handlePerilWeightingChange = (weights: PerilWeighting) => {
    const newLoadingConfig: LoadingConfig = {
      ...productConfig.loadingConfig,
      perilWeighting: weights
    };
    
    onChange({
      ...productConfig,
      loadingConfig: newLoadingConfig
    });
  };

  const handleCoverageLevelLimitsChange = (limits: CoverageLevelLimits) => {
    const newLoadingConfig: LoadingConfig = {
      ...productConfig.loadingConfig,
      coverageLevelLimits: limits
    };
    
    onChange({
      ...productConfig,
      loadingConfig: newLoadingConfig
    });
  };

  // Error categorization
  const getTabErrors = (tabName: string) => {
    switch (tabName) {
      case 'coverage':
        return errors.filter(e => e.path.includes('coverageConfig'));
      // Removed: pricing tab error handling
      case 'loading':
        return errors.filter(e => e.path.includes('loadingConfig'));
      case 'stakeholders':
        return errors.filter(e => e.path.includes('stakeholdersAnrpDistribution'));
      default:
        return [];
    }
  };

  if (loading) {
    return (
      <div className="space-y-8">
        {Array.from({ length: 3 }).map((_, i) => (
          <Card key={i}>
            <CardContent className="p-8">
              <div className="animate-pulse space-y-4">
                <div className="h-6 bg-gray-200 rounded w-1/3"></div>
                <div className="space-y-2">
                  <div className="h-4 bg-gray-200 rounded"></div>
                  <div className="h-4 bg-gray-200 rounded w-2/3"></div>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    );
  }

  if (error) {
    return (
      <Card>
        <CardContent className="p-8 text-center">
          <div className="text-red-600 mb-4">
            Failed to load configuration data: {error}
          </div>
          <Button onClick={() => window.location.reload()}>
            Retry
          </Button>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="grid w-full grid-cols-4">
          {tabs.map((tab) => {
            const Icon = tab.icon;
            const tabErrors = getTabErrors(tab.value);
            return (
              <TabsTrigger key={tab.value} value={tab.value} className="flex items-center gap-2">
                <Icon size={16} />
                <span>{tab.label}</span>
                {tabErrors.length > 0 && (
                  <Badge variant="destructive" className="ml-1 h-5 w-5 p-0 text-xs flex items-center justify-center">
                    {tabErrors.length}
                  </Badge>
                )}
              </TabsTrigger>
            );
          })}
        </TabsList>

        <TabsContent value="overview" className="space-y-6">
          <OverviewForm
            productName={productName}
            productCode={productCode}
            productDescription={productDescription}
            productStatus={productStatus}
            productTags={productTags}
            productVersion={productVersion}
            productCurrency={productConfig.coverageConfig?.currency || ''}
            quotationScript={quotationScript}
            claimsScript={claimsScript}
            onFieldChange={(field, value) => {
              if (field === 'currency') {
                const updatedConfig = {
                  ...productConfig,
                  coverageConfig: {
                    ...productConfig.coverageConfig,
                    currency: value as string
                  }
                };
                onChange(updatedConfig);
              } else {
                (onProductFieldChange || (() => {}))(field, value);
              }
            }}
            readOnly={readOnly}
            automationConfig={productConfig.automationConfig}
            onAutomationConfigChange={(automationConfig: AutomationConfig) => {
              onChange({ ...productConfig, automationConfig });
            }}
            cronExpression={cronExpression}
            onCronExpressionChange={onCronExpressionChange}
          />
        </TabsContent>

        <TabsContent value="coverage" className="space-y-6">
          <CoverageConfigForm
            coverageConfig={productConfig.coverageConfig || {}}
            onChange={handleCoverageConfigChange}
            errors={getTabErrors('coverage')}
            readOnly={readOnly}
            availableAdminUnits={allAdminUnits}
          />
        </TabsContent>

        {/* Removed: Pricing tab content */}

        <TabsContent value="loading" className="space-y-6">
          {/* Loading Configuration Settings */}
          <Card>
            <CardHeader>
              <CardTitle>Loading Configuration Settings</CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Tax Rate (%)
                  </label>
                  <input
                    type="number"
                    value={((productConfig.loadingConfig?.taxRate || 0) * 100).toFixed(1)}
                    onChange={(e) => {
                      if (!readOnly) {
                        const value = parseFloat(e.target.value) / 100;
                        handleLoadingConfigSettingsChange('taxRate', value);
                      }
                    }}
                    min="0"
                    max="100"
                    step="0.1"
                    disabled={readOnly}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Subsidy Rate (%)
                  </label>
                  <input
                    type="number"
                    value={((productConfig.loadingConfig?.subsidyRate || 0) * 100).toFixed(1)}
                    onChange={(e) => {
                      if (!readOnly) {
                        const value = parseFloat(e.target.value) / 100;
                        handleLoadingConfigSettingsChange('subsidyRate', value);
                      }
                    }}
                    min="0"
                    max="100"
                    step="0.1"
                    disabled={readOnly}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Loading Factors Table */}
          <LoadingFactorsTable
            loadingFactors={productConfig.loadingConfig?.loadingFactors || []}
            onChange={handleLoadingFactorsChange}
            availableAdminUnits={adminUnits}
            availablePerils={productConfig.coverageConfig?.perils || []}
            errors={getTabErrors('loading')}
            readOnly={readOnly}
          />

          {/* Peril Weighting */}
          <NewEnforcementTable
            title="Peril Weighting"
            description="Control how weighting factors used in pricing are enforced. These weights determine the relative importance of different perils in premium calculations."
            availablePerils={productConfig.coverageConfig?.perils || []}
            perilWeighting={productConfig.loadingConfig?.perilWeighting || {
              enforcement: "default",
              weights: {}
            }}
            onPerilWeightingChange={handlePerilWeightingChange}
            readOnly={readOnly}
          />

          {/* Coverage Level Limits */}
          <NewEnforcementTable
            title="Coverage Level Limits"
            description="Define the allowed coverage limits for each peril, expressed as return periods (e.g., 5 years means 1-in-5 year event). Must be within the range specified in coverage configuration."
            availablePerils={productConfig.coverageConfig?.perils || []}
            coverageLevelLimits={productConfig.loadingConfig?.coverageLevelLimits || {
              enforcement: "default",
              limits: {}
            }}
            onCoverageLevelLimitsChange={handleCoverageLevelLimitsChange}
            globalLimits={productConfig.coverageConfig?.coverageLevelGlobalLimits}
            readOnly={readOnly}
          />
        </TabsContent>

        <TabsContent value="stakeholders" className="space-y-6">
          <StakeholdersTable
            stakeholders={productConfig.stakeholdersAnrpDistribution || {}}
            onChange={handleStakeholdersChange}
            availableOrganizations={organizations}
            errors={getTabErrors('stakeholders')}
            readOnly={readOnly}
          />
        </TabsContent>
      </Tabs>
    </div>
  );
}
